import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/useAuth";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";

const validationPatterns = {
  password: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{6,}$/,
};

const errorMessages = {
  password: "Password must be at least 6 characters and include uppercase, lowercase, number, and special character",
  confirmPassword: "Passwords do not match",
  token: "Invalid or missing reset token.",
  resetError: "Error resetting password. Please try again.",
};

const ResetPasswordPage = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [alertMessage, setAlertMessage] = useState<{ type: "error"; message: string } | null>(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [, setLocation] = useLocation();
  const { resetPassword, isResetPasswordLoading } = useAuth();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const resetToken = params.get("token");
    if (!resetToken) {
      setAlertMessage({ type: "error", message: errorMessages.token });
    } else {
      setToken(resetToken);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setAlertMessage(null);

    if (!validationPatterns.password.test(password)) {
      setAlertMessage({ type: "error", message: errorMessages.password });
      return;
    }

    if (password !== confirmPassword) {
      setAlertMessage({ type: "error", message: errorMessages.confirmPassword });
      return;
    }

    if (!token) {
      setAlertMessage({ type: "error", message: errorMessages.token });
      return;
    }

    setLoading(true);
    try {
      await resetPassword({ token, password });
      setSuccess(true);
    } catch (error: any) {
      setAlertMessage({ type: "error", message: error.response?.data?.message || errorMessages.resetError });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-blue-600 to-purple-700">
      <Card className="w-full max-w-md mx-4 bg-white/90 backdrop-blur-sm shadow-xl">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl text-center font-bold">Reset Password</CardTitle>
          <p className="text-center text-gray-600">Enter your new password</p>
        </CardHeader>
        <CardContent>
          {alertMessage && (
            <Alert className="mb-4 bg-red-100 text-red-700">
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{alertMessage.message}</AlertDescription>
            </Alert>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              type="password"
              placeholder="New Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <Input
              type="password"
              placeholder="Confirm Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
            <Button
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-700"
              disabled={loading || isResetPasswordLoading}
            >
              {loading || isResetPasswordLoading ? "Resetting..." : "Reset Password"}
            </Button>
          </form>
          <div className="mt-4 text-center text-sm">
            <button className="text-blue-600 hover:underline" onClick={() => setLocation("/login")}>
              Back to Login
            </button>
          </div>
        </CardContent>
      </Card>

      {success && (
        <AlertDialog open={success}>
          <AlertDialogContent>
            <AlertDialogTitle>Password Reset Successful</AlertDialogTitle>
            <AlertDialogDescription>
              Your password has been reset successfully. Redirecting to login...
            </AlertDialogDescription>
            <AlertDialogAction onClick={() => setLocation("/login")}>
              Go to Login
            </AlertDialogAction>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
};

export default ResetPasswordPage;